import { useState, useEffect, useCallback, useRef } from 'react';
import { LyriaClient, LyriaState, WeightedPrompt, MusicConfig } from '@/lib/lyria-client';
import { toast } from 'sonner';
import * as Sentry from "@sentry/react";

interface UseLyriaOptions {
  onPromptFiltered?: (filteredPrompt: string) => void;
}

export type PromptMode = 'replace' | 'additive' | 'blend';

export function useLyria(options: UseLyriaOptions = {}) {
  const [state, setState] = useState<LyriaState>('disconnected');
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);
  const [currentPrompts, setCurrentPrompts] = useState<WeightedPrompt[]>([]);
  const [promptHistory, setPromptHistory] = useState<WeightedPrompt[][]>([]);
  const [volume, setVolumeState] = useState(0.8);
  const [config, setConfig] = useState<MusicConfig>({
    bpm: 120,
    density: 0.65,
    brightness: 0.7,
    temperature: 1.0,
    seed: Math.floor(Math.random() * 0x7fffffff) + 1,
  });
  const configRef = useRef(config);
  
  const clientRef = useRef<LyriaClient | null>(null);
  const audioLevelRef = useRef(0);
  const pendingPromptsRef = useRef<WeightedPrompt[] | null>(null);
  const pendingConfigRef = useRef<Partial<MusicConfig> | null>(null);
  const pendingNeedsResetRef = useRef(false);
  const onPromptFilteredRef = useRef(options.onPromptFiltered);
  const configChangeCountRef = useRef(0);
  const promptsSetCountRef = useRef(0);
  const lastStateRef = useRef<LyriaState>('disconnected');
  const connectPromiseRef = useRef<Promise<boolean> | null>(null);

  const createClient = useCallback(() => {
    return new LyriaClient(
      (newState) => {
        setState(newState);
      },
      (error) => toast.error(error),
      (level) => {
        // Update ref directly to avoid re-renders
        audioLevelRef.current = Math.max(level, audioLevelRef.current);
      },
      (filteredPrompt) => {
        console.log('[useLyria] Prompt filtered, triggering callback');
        Sentry.addBreadcrumb({
          category: "prompt.filter",
          level: "warning",
          message: "prompt filtered by Lyria",
          data: { promptLength: filteredPrompt.length },
        });
        onPromptFilteredRef.current?.(filteredPrompt);
      }
    );
  }, []);

  const getClient = useCallback(() => {
    if (!clientRef.current) {
      clientRef.current = createClient();
    }
    return clientRef.current;
  }, [createClient]);

  // Keep callback ref updated
  useEffect(() => {
    onPromptFilteredRef.current = options.onPromptFiltered;
  }, [options.onPromptFiltered]);

  useEffect(() => {
    if (lastStateRef.current !== state) {
      Sentry.addBreadcrumb({
        category: "lyria.state",
        level: "info",
        message: "state transition",
        data: { from: lastStateRef.current, to: state },
      });
      lastStateRef.current = state;
    }

    const metrics = clientRef.current?.getSentryMetrics?.();
    Sentry.setContext("audio_session", {
      state,
      sessionDurationSeconds: metrics?.sessionDurationSeconds,
      reconnectAttempts: metrics?.reconnectAttempts,
      audioChunksProcessed: metrics?.audioChunksProcessed,
      audioBufferChunks: metrics?.audioBufferChunks,
      totalPromptsSets: promptsSetCountRef.current,
      totalConfigChanges: configChangeCountRef.current,
    });
    Sentry.setTag("sf_state", state);
  }, [state]);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    // Decay audio level smoothly in a background interval
    const interval = setInterval(() => {
      audioLevelRef.current *= 0.85; // Faster decay for snappier visualization
    }, 32);

    return () => {
      clearInterval(interval);
      clientRef.current?.disconnect();
      setAnalyserNode(null);
    };
  }, []);

  const ensureConnected = useCallback(async (): Promise<boolean> => {
    if (
      clientRef.current &&
      state !== 'disconnected' &&
      state !== 'connecting'
    ) {
      return true;
    }

    if (connectPromiseRef.current) {
      return connectPromiseRef.current;
    }

    if (!clientRef.current) {
      clientRef.current = createClient();
    }

    Sentry.addBreadcrumb({
      category: "lyria.websocket",
      level: "info",
      message: "connect requested",
      data: { existingClient: Boolean(clientRef.current) },
    });

    connectPromiseRef.current = (async () => {
      try {
        await clientRef.current?.connect();
        setAnalyserNode(clientRef.current?.getAnalyserNode() ?? null);
        return true;
      } catch (error) {
        toast.error('Failed to connect to Lyria');
        console.error('[useLyria] Connection error:', error);
        Sentry.captureException(error);
        setAnalyserNode(null);
        return false;
      } finally {
        connectPromiseRef.current = null;
      }
    })();

    return connectPromiseRef.current;
  }, [createClient, state]);

  const primeAudio = useCallback(() => {
    void getClient().primeAudio();
  }, [getClient]);

  const setPrompts = useCallback((prompts: WeightedPrompt[]) => {
    promptsSetCountRef.current += 1;
    Sentry.addBreadcrumb({
      category: "prompts",
      level: "info",
      message: "setPrompts",
      data: { count: prompts.length },
    });
    setCurrentPrompts(prompts);
    if (clientRef.current && state !== 'disconnected' && state !== 'connecting') {
      clientRef.current.setPrompts(prompts);
    } else {
      pendingPromptsRef.current = prompts;
    }
  }, [state]);

  const setPromptsWithMode = useCallback((
    prompts: WeightedPrompt[],
    mode: PromptMode = 'replace'
  ) => {
    let finalPrompts: WeightedPrompt[];

    switch (mode) {
      case 'additive': {
        // Additive mode: The AI sees currentPrompts in context and decides what
        // to keep/add. Replace with the AI's complete output to avoid stacking
        // prompts beyond Lyria's optimal 1-2 prompt sweet spot.
        finalPrompts = prompts;
        console.log('[useLyria] Additive mode: replacing with AI-curated prompts', {
          previousCount: currentPrompts.length,
          newCount: finalPrompts.length,
          prompts: finalPrompts.map(p => ({ text: p.text.substring(0, 40), weight: p.weight }))
        });
        Sentry.addBreadcrumb({
          category: "prompts",
          level: "info",
          message: "setPromptsWithMode:additive",
          data: { previousCount: currentPrompts.length, newCount: finalPrompts.length },
        });
        break;
      }

      case 'blend': {
        // Blend mode: The AI already sees currentPrompts in context and outputs
        // a complete replacement set. Replace rather than concatenate to avoid
        // prompt doubling (Lyria works best with 1-2 prompts, not 6).
        finalPrompts = prompts;
        console.log('[useLyria] Blend mode: replacing with AI-curated prompts', {
          previousCount: currentPrompts.length,
          newCount: finalPrompts.length,
          prompts: finalPrompts.map(p => ({ text: p.text.substring(0, 40), weight: p.weight }))
        });
        Sentry.addBreadcrumb({
          category: "prompts",
          level: "info",
          message: "setPromptsWithMode:blend",
          data: { previousCount: currentPrompts.length, newCount: finalPrompts.length },
        });
        break;
      }

      case 'replace':
      default:
        finalPrompts = prompts;
        console.log('[useLyria] Replace mode: using new prompts', finalPrompts);
        Sentry.addBreadcrumb({
          category: "prompts",
          level: "info",
          message: "setPromptsWithMode:replace",
          data: { finalCount: finalPrompts.length },
        });
    }

    // Save current prompts to history before updating
    if (currentPrompts.length > 0) {
      setPromptHistory(prev => [...prev, currentPrompts].slice(-10)); // Keep last 10 versions
    }

    setCurrentPrompts(finalPrompts);

    if (clientRef.current && state !== 'disconnected' && state !== 'connecting') {
      promptsSetCountRef.current += 1;
      clientRef.current.setPrompts(finalPrompts);
    } else {
      pendingPromptsRef.current = finalPrompts;
    }
  }, [currentPrompts, state]);

  const updateConfig = useCallback((newConfig: Partial<MusicConfig>, needsReset = false) => {
    configChangeCountRef.current += 1;
    Sentry.addBreadcrumb({
      category: "config",
      level: "info",
      message: "updateConfig",
      data: { keys: Object.keys(newConfig), needsReset },
    });
    const previousConfig = configRef.current;
    const updated = { ...previousConfig, ...newConfig };
    configRef.current = updated;
    setConfig(updated);

    // Only reset context if the value *actually changes*.
    // Resetting on every suggestion (even unchanged bpm/scale) causes audible pauses/restarts.
    const effectiveNeedsReset =
      needsReset &&
      ((newConfig.bpm !== undefined && newConfig.bpm !== previousConfig.bpm) ||
        (newConfig.scale !== undefined && newConfig.scale !== previousConfig.scale));

    if (clientRef.current && state !== 'disconnected' && state !== 'connecting') {
      clientRef.current.setConfig(updated);
      if (effectiveNeedsReset) {
        Sentry.addBreadcrumb({
          category: "config",
          level: "info",
          message: "resetContext due to config change",
        });
        clientRef.current.resetContext();
      }
    } else {
      pendingConfigRef.current = { ...pendingConfigRef.current, ...newConfig };
      pendingNeedsResetRef.current = pendingNeedsResetRef.current || effectiveNeedsReset;
    }
  }, [state]);

  // Auto-connect and play with pending prompts/config
  const playWithAutoConnect = useCallback(async () => {
    Sentry.addBreadcrumb({ category: "transport", level: "info", message: "play requested" });
    primeAudio();
    const connected = await ensureConnected();
    if (!connected) return;

    // Apply pending prompts and config after connection
    if (pendingPromptsRef.current) {
      clientRef.current?.setPrompts(pendingPromptsRef.current);
      pendingPromptsRef.current = null;
    }
    if (pendingConfigRef.current) {
      const updated = { ...configRef.current, ...pendingConfigRef.current };
      configRef.current = updated;
      setConfig(updated);
      clientRef.current?.setConfig(updated);
      pendingConfigRef.current = null;
      if (pendingNeedsResetRef.current) {
        clientRef.current?.resetContext();
        pendingNeedsResetRef.current = false;
      }
    }

    clientRef.current?.play();
  }, [ensureConnected, primeAudio]);

  const pause = useCallback(() => {
    Sentry.addBreadcrumb({ category: "transport", level: "info", message: "pause requested" });
    clientRef.current?.pause();
  }, []);

  const stop = useCallback(() => {
    Sentry.addBreadcrumb({ category: "transport", level: "info", message: "stop requested" });
    clientRef.current?.stop();
  }, []);

  const resetContext = useCallback(() => {
    Sentry.addBreadcrumb({ category: "transport", level: "info", message: "resetContext requested" });
    clientRef.current?.resetContext();
  }, []);

  const disconnect = useCallback(() => {
    Sentry.addBreadcrumb({ category: "transport", level: "info", message: "disconnect requested" });
    clientRef.current?.disconnect();
    clientRef.current = null;
    setAnalyserNode(null);
  }, []);

  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(newVolume);
    clientRef.current?.setVolume(newVolume);
  }, []);

  return {
    state,
    audioLevelRef,
    analyserNode,
    currentPrompts,
    promptHistory,
    config,
    volume,
    setPrompts,
    setPromptsWithMode,
    updateConfig,
    setVolume,
    primeAudio,
    play: playWithAutoConnect,
    pause,
    stop,
    resetContext,
    disconnect,
  };
}
